import { create } from 'zustand';
import { planService } from '../services/planService';
import {
  AdminSubscriptionPlan,
  PlanDetailResponse,
  CreatePlanRequest,
  UpdatePlanRequest,
  CreatePlanVariantRequest,
  UpdatePlanVariantRequest,
  ApiError,
  PlanFeatures,
} from '../types/api';

interface PlanState {
  // State
  plans: AdminSubscriptionPlan[];
  selectedPlan: PlanDetailResponse | null;
  isLoading: boolean;
  error: ApiError | null;

  // Actions
  fetchPlans: () => Promise<void>;
  fetchPlanById: (id: string) => Promise<void>;
  selectPlan: (plan: PlanDetailResponse | null) => void;
  createPlan: (planData: CreatePlanRequest) => Promise<void>;
  updatePlan: (id: string, planData: UpdatePlanRequest) => Promise<void>;
  deletePlan: (id: string) => Promise<void>;
  createVariant: (planId: string, variantData: Omit<CreatePlanVariantRequest, 'planId'>) => Promise<void>;
  updateVariant: (planId: string, variantId: string, variantData: UpdatePlanVariantRequest) => Promise<void>;
  deleteVariant: (planId: string, variantId: string) => Promise<void>;
  clearError: () => void;
  clearSelectedPlan: () => void;
}

// Default features for new variants
export const defaultFeatures: PlanFeatures = {
  advanceSearch: false,
  limitedSupport: false,
  limitedChat: false,
  highlightedProfile: false,
  astroMatchmaking: false,
  dedicatedManager: false,
  seniorManager: false,
  arrangedMeetings: false,
  hideProfile: false,
};

export const usePlanStore = create<PlanState>()((set, get) => ({
  // Initial state
  plans: [],
  selectedPlan: null,
  isLoading: false,
  error: null,

  // Actions
  fetchPlans: async () => {
    set({ isLoading: true, error: null });
    try {
      const plans = await planService.getPlans();
      set({
        plans,
        isLoading: false,
      });
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  fetchPlanById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const plan = await planService.getPlanById(id);
      set({
        selectedPlan: plan,
        isLoading: false,
      });
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  selectPlan: (plan: PlanDetailResponse | null) => {
    set({ selectedPlan: plan });
  },

  createPlan: async (planData: CreatePlanRequest) => {
    set({ isLoading: true, error: null });
    try {
      await planService.createPlan(planData);
      // Refresh plans list after creation
      await get().fetchPlans();
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  updatePlan: async (id: string, planData: UpdatePlanRequest) => {
    set({ isLoading: true, error: null });
    try {
      const updatedPlan = await planService.updatePlan(id, planData);
      // Update the plan in local state
      set((state) => ({
        plans: state.plans.map((p) => (p.planId === id ? { ...p, ...updatedPlan } : p)),
        selectedPlan: state.selectedPlan?.planId === id ? { ...state.selectedPlan, ...updatedPlan } : state.selectedPlan,
        isLoading: false,
      }));
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  deletePlan: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await planService.deletePlan(id);
      // Remove from local state
      set((state) => ({
        plans: state.plans.filter((p) => p.planId !== id),
        selectedPlan: state.selectedPlan?.planId === id ? null : state.selectedPlan,
        isLoading: false,
      }));
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  createVariant: async (planId: string, variantData: Omit<CreatePlanVariantRequest, 'planId'>) => {
    set({ isLoading: true, error: null });
    try {
      await planService.createVariant(planId, variantData);
      if (get().selectedPlan?.planId === planId) {
        await get().fetchPlanById(planId);
      } else {
        set({ isLoading: false });
      }
      await get().fetchPlans();
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  updateVariant: async (planId: string, variantId: string, variantData: UpdatePlanVariantRequest) => {
    set({ isLoading: true, error: null });
    try {
      await planService.updateVariant(planId, variantId, variantData);
      if (get().selectedPlan?.planId === planId) {
        await get().fetchPlanById(planId);
      } else {
        set({ isLoading: false });
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  deleteVariant: async (planId: string, variantId: string) => {
    set({ isLoading: true, error: null });
    try {
      await planService.deleteVariant(planId, variantId);
      if (get().selectedPlan?.planId === planId) {
        await get().fetchPlanById(planId);
      } else {
        set({ isLoading: false });
      }
      await get().fetchPlans();
    } catch (error) {
      set({
        isLoading: false,
        error: error as ApiError,
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  clearSelectedPlan: () => {
    set({ selectedPlan: null });
  },
}));

